import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateEntryName } from '../folders';

/**
 * Names a new folder in the current one.
 *
 * The name is checked as it is typed rather than on submit — refusing a name
 * only after the reader has committed to it wastes the round trip and tells
 * them nothing they could not have been told immediately.
 */
export function NewFolderDialog({
  open,
  existingNames,
  onCreate,
  onCancel,
}: {
  open: boolean;
  /** Everything already in this folder, so a collision is caught before the request. */
  existingNames: string[];
  onCreate: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const problem = validateEntryName('directory', name, existingNames);
  // Nothing typed yet is not a mistake, so it reads as an empty form rather
  // than as an error the reader has already made.
  const showProblem = name !== '' && problem !== undefined;

  function close() {
    setName('');
    onCancel();
  }

  function submit() {
    if (problem !== undefined) return;
    const created = name.trim();
    setName('');
    onCreate(created);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>
            The folder is created in the folder you are looking at. It stays
            empty until you put something in it.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Label htmlFor="new-folder-name">Folder name</Label>
          <Input
            id="new-folder-name"
            autoFocus
            value={name}
            aria-invalid={showProblem}
            aria-describedby={showProblem ? 'new-folder-problem' : undefined}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {showProblem && (
            <p id="new-folder-problem" className="text-destructive text-sm">
              {problem}
            </p>
          )}
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button disabled={problem !== undefined} onClick={submit}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
